<script setup lang="ts">
/**
 * A repo's branches and the links between them, in the Repos view: which
 * branches it has (main and staging always), which may be merged into which —
 * each direction of a link By hand or Auto — which are pushed, deployed and
 * tested on their own, which URLs each is served at, and where the Claude
 * worktrees' work goes. Saved as it changes, like the auto boxes above it
 * (`PUT /api/tmux/repos/:name/branches`); the whole-set Save never carries it.
 *
 * Staging → main's Auto is "Auto deploy to MAIN": main moves only on a Deploy,
 * so that is how staging gets there. Main's own "auto deploy" is the repo's
 * Deploy whenever main moves; any other branch's is the box's
 * `deploy-<repo>-<branch>` script (server/branchRuns.ts), offered only where
 * there is one. A failed Deploy unticks both of main's, which is why they are
 * read from the server's deploy status where there is one — and a failed
 * branch deploy its own, which is why a new failure rereads the settings.
 */
import { computed, ref, watch } from "vue";
import RepoBranchGraph from "./RepoBranchGraph.vue";
import { loadRepoInfos, setBranches } from "../lib/repoSettings.js";
import { api } from "../lib/api.js";
import { deployStatusChanged, deployStatuses } from "../lib/deploys.js";
import { toast } from "../lib/toast.js";
import {
  REPO_BRANCH_URLS_MAX,
  STAGING_BRANCH,
  TRUNK_NODE,
  WORKTREES_NODE,
  branchDeployTag,
  branchScriptSlug,
  branchesOf,
  normalizeBranchUrl,
  testCommandOf,
  withFlag,
  withPush,
  repoBranchProblem,
  repoMergeProblem,
  type RepoBranch,
  type RepoBranchSet,
  type RepoInfo,
  type RepoMerge,
} from "@shared/repoSettings.js";
import type { BranchRun } from "@shared/deploy.js";

const props = defineProps<{ repo: RepoInfo }>();

/** The set as saved, with staging → main's auto and main's auto deploy as the server last said. */
const set = computed((): RepoBranchSet => {
  const saved = branchesOf(props.repo.settings);
  const st = deployStatuses.value.get(props.repo.name);
  if (!st) return saved;
  return {
    branches: saved.branches.map((b) => (b.name === TRUNK_NODE && !!b.autoDeploy !== st.auto ? withFlag(b, "autoDeploy", st.auto) : b)),
    merges: saved.merges.map((m): RepoMerge => {
      if (m.from !== STAGING_BRANCH || m.into !== TRUNK_NODE || !!m.auto === st.autoStaging) return m;
      return st.autoStaging ? { ...m, auto: m.auto ?? Date.now() } : { from: m.from, into: m.into };
    }),
  };
});

const fixed = (name: string): boolean => name === TRUNK_NODE || name === STAGING_BRANCH;
/** How a direction goes: not at all, only by a pill's menu, or on its own. */
type Way = "off" | "hand" | "auto";
const WAYS: { value: Way; label: string }[] = [
  { value: "off", label: "—" },
  { value: "hand", label: "By hand" },
  { value: "auto", label: "Auto" },
];

function wayOf(from: string, into: string): Way {
  const m = set.value.merges.find((l) => l.from === from && l.into === into);
  return !m ? "off" : m.auto ? "auto" : "hand";
}

const saving = ref(false);

async function save(next: RepoBranchSet): Promise<void> {
  saving.value = true;
  try {
    await setBranches(props.repo.name, next);
    const st = deployStatuses.value.get(props.repo.name);
    const staging = next.merges.some((m) => m.from === STAGING_BRANCH && m.into === TRUNK_NODE && m.auto);
    const auto = next.branches.some((b) => b.name === TRUNK_NODE && b.autoDeploy);
    if (st && (st.autoStaging !== staging || st.auto !== auto)) deployStatusChanged({ ...st, autoStaging: staging, auto });
  } catch (err) {
    toast(err instanceof Error ? err.message : String(err));
  } finally {
    saving.value = false;
  }
}

/** The set with `from` → `into` going `way`. Auto's time is the server's to keep or set. */
function withWay(s: RepoBranchSet, from: string, into: string, way: Way): RepoBranchSet {
  const rest = s.merges.filter((m) => !(m.from === from && m.into === into));
  const had = s.merges.find((m) => m.from === from && m.into === into);
  if (way === "off") return { branches: s.branches, merges: rest };
  const link: RepoMerge = way === "auto" ? { from, into, auto: had?.auto ?? Date.now() } : { from, into };
  return { branches: s.branches, merges: had ? s.merges.map((m) => (m === had ? link : m)) : [...rest, link] };
}

function setWay(from: string, into: string, ev: Event): void {
  void save(withWay(set.value, from, into, (ev.target as HTMLSelectElement).value as Way));
}

// ── the worktrees ────────────────────────────────────────────────────────
const worktreeLink = computed(() => set.value.merges.find((m) => m.from === WORKTREES_NODE) ?? null);
const worktreeInto = computed(() => worktreeLink.value?.into ?? STAGING_BRANCH);
/** Anywhere but main, which moves only on a Deploy. */
const worktreeTargets = computed(() => set.value.branches.filter((b) => b.name !== TRUNK_NODE).map((b) => b.name));

function setWorktreeInto(ev: Event): void {
  const into = (ev.target as HTMLSelectElement).value;
  const auto = worktreeLink.value?.auto;
  const merges = set.value.merges.filter((m) => m.from !== WORKTREES_NODE);
  void save({ branches: set.value.branches, merges: [...merges, { from: WORKTREES_NODE, into, ...(auto ? { auto } : {}) }] });
}

function setWorktreeAuto(ev: Event): void {
  void save(withWay(set.value, WORKTREES_NODE, worktreeInto.value, (ev.target as HTMLInputElement).checked ? "auto" : "hand"));
}

// ── branches ─────────────────────────────────────────────────────────────
/** Each pair of branches a link joins, once, under whichever comes first. */
const pairsUnder = (name: string): string[] => {
  const order = set.value.branches.map((b) => b.name);
  const at = order.indexOf(name);
  const others = new Set<string>();
  for (const m of set.value.merges) {
    if (m.from === WORKTREES_NODE) continue;
    const other = m.from === name ? m.into : m.into === name ? m.from : null;
    if (other && order.indexOf(other) > at) others.add(other);
  }
  return order.filter((o) => others.has(o));
};

/** Branches `name` has no link with yet. */
const unlinked = (name: string): string[] =>
  set.value.branches
    .map((b) => b.name)
    .filter((o) => o !== name && !set.value.merges.some((m) => (m.from === name && m.into === o) || (m.from === o && m.into === name)));

/** A new link: `name` into `other` by hand where that may go, else the other way. */
function addLink(name: string, ev: Event): void {
  const select = ev.target as HTMLSelectElement;
  const other = select.value;
  select.value = "";
  if (!other) return;
  const [from, into] = repoMergeProblem(name, other) ? [other, name] : [name, other];
  if (repoMergeProblem(from, into)) return;
  void save(withWay(set.value, from, into, "hand"));
}

function dropLink(a: string, b: string): void {
  void save({ branches: set.value.branches, merges: set.value.merges.filter((m) => !((m.from === a && m.into === b) || (m.from === b && m.into === a))) });
}

function setPush(name: string, ev: Event): void {
  const on = (ev.target as HTMLInputElement).checked;
  void save({
    merges: set.value.merges,
    branches: set.value.branches.map((b) => (b.name === name ? withPush(b, on) : b)),
  });
}

// ── deploys and tests ────────────────────────────────────────────────────
function setFlag(name: string, key: "autoPull" | "autoDeploy" | "test", ev: Event): void {
  const on = (ev.target as HTMLInputElement).checked;
  void save({
    merges: set.value.merges,
    branches: set.value.branches.map((b) => (b.name === name ? withFlag(b, key, on) : b)),
  });
}

const pullTitle = (b: RepoBranch): string =>
  `The git server's copy merged in whenever it has commits ${b.name} lacks` +
  (b.name === TRUNK_NODE ? ", while the checkout has it out" : b.name === STAGING_BRANCH ? "" : ", while no tree has it out") +
  "; a conflict merges nothing, and auto-fix sets a Claude on it";

/** Whether `b` can be deployed: main by the repo's Deploy, any other by a script of its own. */
const deployable = (b: RepoBranch): boolean => b.name === TRUNK_NODE || !!props.repo.branchDeploys?.includes(branchScriptSlug(b.name));
const scriptName = (name: string): string => `/usr/local/sbin/deploy-${props.repo.name}-${branchScriptSlug(name)}`;

const deployTitle = (b: RepoBranch): string => {
  const gated = b.test ? `\n\nWith "test" ticked, only a tip whose tests passed goes out` : "";
  return deployWhat(b) + (deployable(b) ? gated : "");
};
const deployWhat = (b: RepoBranch): string => {
  if (b.name === TRUNK_NODE) return "Deploy runs on its own whenever main moves past what is live (the deployed tag). A failed one unticks it";
  if (!deployable(b)) return `Put an executable ${scriptName(b.name)} on the box (and a sudo rule for claude) to deploy ${b.name}`;
  return `${scriptName(b.name)} runs whenever ${b.name} moves past what is live (the ${branchDeployTag(b.name)} tag), after ${b.name} is pushed. A failed one unticks it`;
};
const testTitle = (b: RepoBranch): string =>
  `Run \`${testCommandOf(props.repo.settings)}\` on each new tip of ${b.name}, in a throwaway tree after the worktree create hook — and with "auto deploy" ticked, deploy only a tip they passed on. With auto-fix on, a failure gets a Claude off ${b.name}`;

/** Each branch's last run of each kind, from the server's deploy status. */
const runs = computed(() => deployStatuses.value.get(props.repo.name)?.branches ?? []);
const runOf = (name: string, kind: BranchRun["kind"]): BranchRun | undefined => runs.value.find((r) => r.branch === name && r.kind === kind);
const ago = (ms: number): string => {
  const s = Math.max(0, Math.round((Date.now() - ms) / 1000));
  if (s < 60) return "just now";
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
};
const runText = (r: BranchRun): string => {
  const what = r.kind === "deploy" ? (r.ok === null ? "deploying" : r.ok ? "deployed" : "deploy failed") : r.ok === null ? "testing" : r.ok ? "tests passed" : "tests failed";
  return `${what} ${r.sha.slice(0, 7)}${r.ok === null ? "…" : ` · ${ago(r.at)}`}`;
};
const busy = computed(() => runs.value.some((r) => r.ok === null));

// A branch deploy that failed unticked its box on the server: read the settings again.
watch(
  () => runs.value.filter((r) => r.kind === "deploy" && r.ok === false).map((r) => `${r.branch}@${r.at}`).join(),
  (now, was) => {
    if (was !== undefined && now !== was) void loadRepoInfos();
  },
);

const starting = ref<string | null>(null);
async function runNow(name: string, kind: BranchRun["kind"]): Promise<void> {
  starting.value = `${name}/${kind}`;
  try {
    await api.post(`/api/tmux/repos/${encodeURIComponent(props.repo.name)}/branches/${kind}/run`, { branch: name });
    toast(kind === "deploy" ? `Deploying ${name}` : `Testing ${name}`);
  } catch (err) {
    toast(err instanceof Error ? err.message : String(err));
  } finally {
    starting.value = null;
  }
}

// ── URLs ─────────────────────────────────────────────────────────────────
/** The URL box being typed in, per branch. */
const newUrl = ref<Record<string, string>>({});
const urlProblem = (name: string): string | null => {
  const text = newUrl.value[name]?.trim();
  if (!text) return null;
  const url = normalizeBranchUrl(text);
  if (!url) return "not an http(s) URL";
  return set.value.branches.find((b) => b.name === name)?.urls?.includes(url) ? "already here" : null;
};

function withUrls(name: string, urls: (have: string[]) => string[]): RepoBranchSet {
  return {
    merges: set.value.merges,
    branches: set.value.branches.map((b) => {
      if (b.name !== name) return b;
      const { urls: had, ...rest } = b;
      const next = urls(had ?? []);
      return next.length ? { ...rest, urls: next } : rest;
    }),
  };
}

function addUrl(name: string): void {
  const url = normalizeBranchUrl(newUrl.value[name] ?? "");
  if (!url || urlProblem(name)) return;
  newUrl.value[name] = "";
  void save(withUrls(name, (have) => [...have, url]));
}

function dropUrl(name: string, url: string): void {
  void save(withUrls(name, (have) => have.filter((u) => u !== url)));
}

const newBranch = ref("");
const newProblem = computed(() => {
  const name = newBranch.value.trim();
  if (!name) return null;
  if (set.value.branches.some((b) => b.name === name)) return "already here";
  return repoBranchProblem(name);
});

function addBranch(): void {
  const name = newBranch.value.trim();
  if (!name || newProblem.value) return;
  newBranch.value = "";
  // Merged from staging by hand to start with: where a new branch's work most often comes from.
  void save({
    branches: [...set.value.branches, { name }],
    merges: [...set.value.merges, { from: STAGING_BRANCH, into: name }],
  });
}

function dropBranch(name: string): void {
  // The worktrees' link falls back to staging rather than go.
  const merges = set.value.merges
    .map((m) => (m.from === WORKTREES_NODE && m.into === name ? { ...m, into: STAGING_BRANCH } : m))
    .filter((m) => m.from !== name && m.into !== name);
  void save({ branches: set.value.branches.filter((b) => b.name !== name), merges });
}

const wayTitle = (from: string, into: string): string => {
  const problem = repoMergeProblem(from, into);
  if (problem) return problem;
  if (into === TRUNK_NODE) return `${from} → ${into}. Auto is "Auto deploy to MAIN": Deploy runs whenever ${from} has commits ${into} lacks, merging them in first.`;
  return `${from} → ${into}: By hand, its pill's menu offers the merge; Auto, it is merged whenever ${from} has commits ${into} lacks. A conflict merges nothing, and auto-fix sets a Claude on it.`;
};
</script>

<template>
  <section class="repo-branches" :aria-label="`${repo.name} branches`" :aria-busy="saving">
    <h3>Branches</h3>
    <div class="repo-branches-body">
      <RepoBranchGraph :set="set" />
      <div class="repo-branch-list">
        <div class="repo-branch">
          <div class="repo-branch-head">
            <span class="repo-branch-name worktrees" title="Every Claude worktree: where its commits go">worktrees</span>
            <span aria-hidden="true">→</span>
            <select :value="worktreeInto" aria-label="Where a worktree's work goes" :disabled="saving" @change="setWorktreeInto">
              <option v-for="b in worktreeTargets" :key="b" :value="b">{{ b }}</option>
            </select>
            <label class="repo-branch-check" title="A commit in a worktree is merged on its own; a conflict merges nothing, and auto-fix sets a Claude on it">
              <input type="checkbox" :checked="!!worktreeLink?.auto" :disabled="saving" @change="setWorktreeAuto" />
              auto merge
            </label>
          </div>
        </div>

        <div v-for="b in set.branches" :key="b.name" class="repo-branch">
          <div class="repo-branch-head">
            <span class="repo-branch-name" :title="b.name === TRUNK_NODE ? 'The trunk: main, or master where there is no main' : undefined">{{ b.name }}</span>
            <label class="repo-branch-check" title="Pushed to the git server whenever the checkout has it ahead, never forced">
              <input type="checkbox" :checked="!!b.autoPush" :disabled="saving" @change="setPush(b.name, $event)" />
              auto push
            </label>
            <label class="repo-branch-check" :title="pullTitle(b)">
              <input type="checkbox" :checked="!!b.autoPull" :disabled="saving" @change="setFlag(b.name, 'autoPull', $event)" />
              auto pull
            </label>
            <label class="repo-branch-check" :title="deployTitle(b)">
              <input type="checkbox" :checked="!!b.autoDeploy" :disabled="saving || (!deployable(b) && !b.autoDeploy)" @change="setFlag(b.name, 'autoDeploy', $event)" />
              auto deploy
            </label>
            <label class="repo-branch-check" :title="testTitle(b)">
              <input type="checkbox" :checked="!!b.test" :disabled="saving" @change="setFlag(b.name, 'test', $event)" />
              test
            </label>
            <span style="flex: 1"></span>
            <select
              v-if="unlinked(b.name).length"
              class="repo-branch-add"
              value=""
              :aria-label="`Link ${b.name} with another branch`"
              :disabled="saving"
              @change="addLink(b.name, $event)"
            >
              <option value="">+ link…</option>
              <option v-for="o in unlinked(b.name)" :key="o" :value="o">{{ o }}</option>
            </select>
            <button v-if="!fixed(b.name)" class="btn ghost danger" type="button" :title="`Remove ${b.name} and its links (the branch itself stays)`" :disabled="saving" @click="dropBranch(b.name)">✕</button>
          </div>
          <div v-for="o in pairsUnder(b.name)" :key="o" class="repo-branch-link">
            <label :title="wayTitle(b.name, o)">
              <span>→ {{ o }}</span>
              <select :value="wayOf(b.name, o)" :aria-label="`${b.name} into ${o}`" :disabled="saving || !!repoMergeProblem(b.name, o)" @change="setWay(b.name, o, $event)">
                <option v-for="w in WAYS" :key="w.value" :value="w.value">{{ w.label }}</option>
              </select>
            </label>
            <label :title="wayTitle(o, b.name)">
              <span>← {{ o }}</span>
              <select :value="wayOf(o, b.name)" :aria-label="`${o} into ${b.name}`" :disabled="saving || !!repoMergeProblem(o, b.name)" @change="setWay(o, b.name, $event)">
                <option v-for="w in WAYS" :key="w.value" :value="w.value">{{ w.label }}</option>
              </select>
            </label>
            <button class="btn ghost" type="button" :title="`Unlink ${b.name} and ${o}`" :disabled="saving" @click="dropLink(b.name, o)">✕</button>
          </div>
          <div v-if="(b.name !== TRUNK_NODE && deployable(b)) || b.test || runOf(b.name, 'deploy') || runOf(b.name, 'test')" class="repo-branch-runs">
            <template v-for="kind in ['deploy', 'test'] as const" :key="kind">
              <span
                v-if="runOf(b.name, kind)"
                class="repo-branch-run"
                :class="{ ok: runOf(b.name, kind)!.ok === true, failed: runOf(b.name, kind)!.ok === false, running: runOf(b.name, kind)!.ok === null }"
                :title="runOf(b.name, kind)!.failure ?? undefined"
              >{{ runText(runOf(b.name, kind)!) }}</span>
            </template>
            <button
              v-if="b.name !== TRUNK_NODE && deployable(b)"
              class="btn ghost"
              type="button"
              :title="`Run ${scriptName(b.name)} now, on ${b.name}'s tip`"
              :disabled="busy || starting !== null"
              @click="runNow(b.name, 'deploy')"
            >Deploy now</button>
            <button
              class="btn ghost"
              type="button"
              :title="`Run the tests now on ${b.name}'s tip`"
              :disabled="busy || starting !== null"
              @click="runNow(b.name, 'test')"
            >Test now</button>
          </div>
          <div class="repo-branch-urls">
            <span v-for="u in b.urls ?? []" :key="u" class="repo-branch-url">
              <a :href="u" target="_blank" rel="noopener" :title="`${b.name} is served at ${u}`">{{ u.replace(/^https:\/\//, "") }}</a>
              <button class="btn ghost" type="button" :title="`Remove ${u} from ${b.name}`" :aria-label="`Remove ${u}`" :disabled="saving" @click="dropUrl(b.name, u)">✕</button>
            </span>
            <form v-if="(b.urls?.length ?? 0) < REPO_BRANCH_URLS_MAX" class="repo-branch-url-add" @submit.prevent="addUrl(b.name)">
              <input
                v-model="newUrl[b.name]"
                :placeholder="b.urls?.length ? '+ URL' : `URL ${b.name} is served at`"
                :aria-label="`A URL ${b.name} is served at`"
                :aria-invalid="!!urlProblem(b.name)"
                :title="urlProblem(b.name) ?? 'Where this branch is live, e.g. staging.example.com — Enter adds it'"
                autocomplete="off"
                spellcheck="false"
                inputmode="url"
                maxlength="500"
              />
            </form>
          </div>
        </div>

        <form class="repo-ask repo-branch-new" @submit.prevent="addBranch">
          <input
            v-model="newBranch"
            placeholder="New branch, e.g. release"
            aria-label="Add a branch"
            autocomplete="off"
            spellcheck="false"
            maxlength="100"
            :aria-invalid="!!newProblem"
          />
          <button class="btn" type="submit" :disabled="!newBranch.trim() || !!newProblem || saving">+ Branch</button>
        </form>
        <p v-if="newProblem" class="money-hint repo-branch-problem">{{ newBranch.trim() }}: {{ newProblem }}</p>
      </div>
    </div>
  </section>
</template>
